// The contacts we already hold for a carrier.
//
// This is the intervention the redesign exists for. 38.8% of VoE requests were
// created with no contact method at all, and the old screen only offered a
// contact autocomplete after a DOT number had been matched — buried in an
// optional section most drivers never opened. Here the contacts arrive with the
// employer, the first is already selected, and continuing accepts it.
//
// Only a carrier picked from the registry has any: an employer the driver typed
// has no DOT number to look them up by.
//
// The same lookup says whether we need the driver to find one at all. A carrier
// marked platform-only answers verifications through a verification platform,
// so a driver is never asked to type a contact for it — but one we already hold
// is still offered. Read from directContactNeeded, the one routing field a
// driver can see: platformOnly and verificationPlatforms are redacted for them.

import { useMemo } from 'react'

import { useQuery } from '@apollo/client'

import { GET_CARRIER_BY_DOT_NUMBER } from 'src/graphql/carriers.graphql'

import type { ContactChoice } from './entryFlow'

interface CarrierContact {
  contactId?: string | null
  contactName?: string | null
  phoneNumber?: string | null
  email?: string | null
  occurrenceCount?: number | null
}

export interface CarrierContacts {
  contacts: ContactChoice[]
  loading: boolean
  /** The carrier answers verifications only through a platform. */
  platformOnly: boolean
}

/** Something to reach them by. A name alone is not a contact method. */
const isReachable = (contact: CarrierContact): boolean =>
  Boolean(contact.phoneNumber?.trim() || contact.email?.trim())

export const useCarrierContacts = (
  dotNumber?: string | null
): CarrierContacts => {
  const dot = dotNumber?.trim()
  const { data, loading } = useQuery(GET_CARRIER_BY_DOT_NUMBER, {
    variables: { dotNumber: dot },
    skip: !dot,
  })

  const contacts = useMemo(() => {
    const held: CarrierContact[] = data?.carrierByDotNumber?.contacts ?? []
    return (
      held
        .filter(isReachable)
        // Most-seen first, so the one pre-selected is the one we have reached
        // most often rather than whichever the database returned first
        .sort((a, b) => (b.occurrenceCount ?? 0) - (a.occurrenceCount ?? 0))
        .map(
          (contact): ContactChoice => ({
            contactId: contact.contactId ?? null,
            name: contact.contactName ?? null,
            phone: contact.phoneNumber ?? null,
            email: contact.email ?? null,
            source: 'known_chosen',
          })
        )
    )
  }, [data])

  // Only an explicit false: a carrier we do not have, or an answer without the
  // field, means ask as usual
  const platformOnly = Boolean(
    dot && data?.carrierByDotNumber?.directContactNeeded === false
  )

  return {
    contacts,
    loading: Boolean(dot) && loading,
    platformOnly,
  }
}
